import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { gsap } from 'gsap';

import OliviaWavingSvg from '../../svgs/olivia/olivia-waving.svg';
import OliviaWalkSvg from '../../svgs/olivia/olivia-walk-all-books.svg';
import OliviaCelebratingSvg from '../../svgs/olivia/olivia-celebrate.svg';
import OliviaDoneSvg from '../../svgs/olivia/olivia-done.svg';

const TOTAL_BOOKS = 10;

export const POSES = {
  WAVE: 'wave',
  WALK: 'walk',
  CELEBRATE: 'celebrate',
  DONE: 'done',
};

const buildTimeline = (pose, container, { onComplete } = {}) => {
  if (!container) return null;

  switch (pose) {
    case POSES.WAVE: {
      const lowerArm = container.querySelector('#lowerarm-left');
      const hand = container.querySelector('#hand-left');

      if (!lowerArm || !hand) return null;

      gsap.set(lowerArm, { transformOrigin: '50% 100%' });
      gsap.set(hand, { transformOrigin: '50% 100%' });

      const tl = gsap
        .timeline({ paused: true, repeat: 3 })
        .to(lowerArm, { rotation: 2, duration: 0.2 }, 0)
        .to(lowerArm, { rotation: -6, duration: 0.4 }, 0.2)
        .to(lowerArm, { rotation: 0, duration: 0.2 }, 0.6)
        .to(hand, { rotation: 2, duration: 0.2 }, 0)
        .to(hand, { rotation: -11, duration: 0.4 }, 0.2)
        .to(hand, { rotation: 0, duration: 0.2 }, 0.6)
        .call(() => {
          onComplete?.();
        });

      return tl;
    }
    case POSES.WALK: {
      const legLeft = container.querySelector('#leg-left');
      const legRight = container.querySelector('#leg-right');
      const lowerlegLeft = container.querySelector('#lowerleg-left');
      const lowerlegRight = container.querySelector('#lowerleg-right');

      if (!legLeft || !legRight || !lowerlegLeft || !lowerlegRight) return null;

      gsap.set(legLeft, { transformOrigin: '50% 0%', willChange: 'transform' });
      gsap.set(legRight, { transformOrigin: '50% 0%', willChange: 'transform' });
      gsap.set(lowerlegLeft, { transformOrigin: '50% 0%' });
      gsap.set(lowerlegRight, { transformOrigin: '50% 0%' });

      const tl = gsap
        .timeline({ paused: true, repeat: -1 })
        // leg-left: starts back, swings forward, then back (hip pivot)
        .fromTo(legLeft, { rotation: -10 }, { rotation: 20, duration: 0.5, ease: 'sine.inOut' }, 0)
        .to(legLeft, { rotation: -10, duration: 0.5, ease: 'sine.inOut' }, 0.5)
        // lowerleg-left: knee bends when leg is back, straightens when forward
        .fromTo(lowerlegLeft, { rotation: 8 }, { rotation: 0, duration: 0.5, ease: 'sine.inOut' }, 0)
        .to(lowerlegLeft, { rotation: 8, duration: 0.5, ease: 'sine.inOut' }, 0.5)
        // leg-right: opposite phase (hip pivot)
        .fromTo(legRight, { rotation: 2 }, { rotation: -22, duration: 0.5, ease: 'sine.inOut' }, 0)
        .to(legRight, { rotation: 2, duration: 0.5, ease: 'sine.inOut' }, 0.5)
        // lowerleg-right: knee bends when leg is back, straightens when forward
        .fromTo(lowerlegRight, { rotation: 0 }, { rotation: 8, duration: 0.5, ease: 'sine.inOut' }, 0)
        .to(lowerlegRight, { rotation: 0, duration: 0.5, ease: 'sine.inOut' }, 0.5);

      return tl;
    }
    case POSES.CELEBRATE: {
      const armLeft = container.querySelector('#arm-left');
      const armRight = container.querySelector('#arm-right');

      if (!armLeft || !armRight) return null;

      gsap.set(armLeft, { transformOrigin: '0% 0%' });
      gsap.set(armRight, { transformOrigin: '100% 0%' });

      const tl = gsap
        .timeline({ paused: true })
        .to(armLeft, { rotation: -11, duration: 0.5, ease: 'sine.inOut', yoyo: true, repeat: 5 }, 0)
        .to(armRight, { rotation: 11, duration: 0.5, ease: 'sine.inOut', yoyo: true, repeat: 5 }, 0)
        .call(() => {
          onComplete?.();
        });

      return tl;
    }
    // POSES.DONE is a static pose with no animation
    default:
      return null;
  }
};

const Olivia = forwardRef(({ currentProgress, goal }, ref) => {
  const containerRef = useRef(null);
  const tlRef = useRef(null);
  const shouldPlayRef = useRef(false);

  const [{ pose, animKey, settings }, setAnim] = useState({ pose: null, animKey: 0, settings: {} });

  const setNewPose = (newPose, animSettings = {}) => {
    shouldPlayRef.current = false;
    setAnim((prev) => ({ pose: newPose, animKey: prev.animKey + 1, settings: animSettings }));
  };

  const playAnimation = (newPose, animSettings = {}) => {
    setNewPose(newPose, animSettings);
    shouldPlayRef.current = true;
  };

  useEffect(() => {
    tlRef.current?.kill();

    const tl = buildTimeline(pose, containerRef.current, settings);
    tlRef.current = tl;

    if (shouldPlayRef.current) {
      tl?.play();
      shouldPlayRef.current = false;
    }
    return () => tl?.kill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pose, animKey]);

  useImperativeHandle(ref, () => ({
    setPose: (newPose, animSettings) => setNewPose(newPose, animSettings),
    play: () => tlRef.current?.play(),
    wave: (animSettings) => playAnimation(POSES.WAVE, animSettings),
    walk: (animSettings) => playAnimation(POSES.WALK, animSettings),
    celebrate: (animSettings) => playAnimation(POSES.CELEBRATE, animSettings),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  const bookCount = goal
    ? Math.min(Math.max(1, Math.ceil(currentProgress / (goal / TOTAL_BOOKS))), TOTAL_BOOKS)
    : 1;

  return (
    <Container ref={containerRef} $flip={settings?.flip} $bookCount={bookCount}>
      {pose === POSES.WAVE && <OliviaWavingSvg />}
      {pose === POSES.WALK && <OliviaWalkSvg />}
      {pose === POSES.CELEBRATE && (
        // Offset aligns the celebrate SVG's visual anchor with the walk SVG
        <div style={{ transform: 'translate(-20px, 2px)' }}>
          <OliviaCelebratingSvg />
        </div>
      )}
      {pose === POSES.DONE && (
        // Offset aligns the done SVG's visual anchor with the walk SVG
        <div style={{ transform: 'translate(-20.5px, 2px)' }}>
          <OliviaDoneSvg />
        </div>
      )}
    </Container>
  );
});

Olivia.propTypes = {
  currentProgress: PropTypes.number.isRequired,
  goal: PropTypes.number,
};

Olivia.displayName = 'Olivia';

export default Olivia;

const flipStyles = {
  left: css`
    transform: scaleX(-1);
  `,
};

const Container = styled.div`
  ${({ $flip }) => flipStyles[$flip]};
  svg {
    overflow: visible;
  }
  /* hide books beyond the current count, leaving only the first $bookCount visible */
  #books > g[id^='book']:nth-child(n + ${(props) => props.$bookCount + 1}) {
    display: none;
  }
`;
